import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Card,
  Button,
  Badge,
  Skeleton,
  Modal,
  Alert,
  ErrorBoundary,
  useToast,
  Timeline,
  ExpenseStatusBadge,
  ApprovalCard,
  ReceiptImageViewer,
} from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { expenseService } from '@/services/expense.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { CATEGORY_LABELS } from '@/types/expense';

// ─── Status accent config ───────────────────────────────────────────────────

const STATUS_ACCENT: Record<string, { bg: string; border: string; darkBg: string }> = {
  PENDING: {
    bg: 'bg-warning-50',
    border: 'border-l-4 border-warning-500',
    darkBg: 'dark:bg-warning-900/20',
  },
  APPROVED: {
    bg: 'bg-success-50',
    border: 'border-l-4 border-success-500',
    darkBg: 'dark:bg-success-900/20',
  },
  REJECTED: {
    bg: 'bg-danger-50',
    border: 'border-l-4 border-danger-500',
    darkBg: 'dark:bg-danger-900/20',
  },
  HOLD: {
    bg: 'bg-amber-50',
    border: 'border-l-4 border-amber-500',
    darkBg: 'dark:bg-amber-900/20',
  },
};

// ─── Category badge color map ───────────────────────────────────────────────

const CATEGORY_BADGE_VARIANT: Record<string, 'info' | 'success' | 'warning' | 'danger' | 'draft'> = {
  RENT: 'info',
  UTILITIES: 'info',
  SALARIES: 'draft',
  MARKETING: 'warning',
  MAINTENANCE: 'draft',
  SUPPLIES: 'success',
  TRANSPORT: 'draft',
  INSURANCE: 'info',
  TAXES_FEES: 'danger',
  OTHER: 'draft',
};

function ExpenseDetailContent() {
  const currency = useTenantCurrency();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profile = useAuthStore((s) => s.profile);

  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canApprove = roleLevel >= ROLE_LEVELS.OWNER;

  const { data: expense, isLoading, error } = useQuery({
    queryKey: ['expense', id],
    queryFn: () => expenseService.getById(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['expense', id] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
  };

  const approveMutation = useMutation({
    mutationFn: () => expenseService.approve(id!),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Expense approved', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => expenseService.reject(id!, reason),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Expense rejected', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  const holdMutation = useMutation({
    mutationFn: () => expenseService.hold(id!),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Expense put on hold', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
  });

  useEffect(() => {
    if (!error) return;
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/expenses');
  }, [error, toast, navigate]);

  if (error) return null;

  if (isLoading || !expense) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton variant="rect" height={300} className="rounded-xl" />
            <Skeleton variant="rect" height={200} className="rounded-xl" />
          </div>
          <div className="space-y-4">
            <Skeleton variant="rect" height={200} className="rounded-xl" />
            <Skeleton variant="rect" height={200} className="rounded-xl" />
          </div>
        </div>
      </>
    );
  }

  const isSelfSubmitted = profile?.id === expense.submitted_by_id;
  const isPending = expense.status === 'PENDING';
  const isOnHold = expense.status === 'HOLD';
  const statusAccent = STATUS_ACCENT[expense.status] ?? STATUS_ACCENT.PENDING;

  // Build timeline
  const timelineItems: TimelineItem[] = [
    {
      id: '1',
      type: 'pending',
      action: 'Expense submitted',
      actor: expense.submitted_by_name,
      timestamp: expense.created_at,
    },
  ];
  if (expense.status === 'APPROVED' && expense.approved_at) {
    timelineItems.push({
      id: '2',
      type: 'approved',
      action: 'Approved',
      actor: expense.approved_by_name ?? undefined,
      timestamp: expense.approved_at,
      description: 'Expense impacts P&L',
    });
  } else if (expense.status === 'REJECTED' && expense.approved_at) {
    timelineItems.push({
      id: '2',
      type: 'rejected',
      action: 'Rejected',
      actor: expense.approved_by_name ?? undefined,
      timestamp: expense.approved_at,
      description: expense.rejection_reason ?? '',
    });
  } else if (expense.status === 'HOLD') {
    timelineItems.push({
      id: '2',
      type: 'inProgress',
      action: 'Put on Hold',
      timestamp: expense.updated_at,
    });
  }

  const isMutating = approveMutation.isPending || rejectMutation.isPending || holdMutation.isPending;

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Expense #{expense.id.slice(0, 8).toUpperCase()}</h1>
            <p className="text-warning-100 mt-1">
              {new Date(expense.expense_date).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums">{formatCurrency(expense.amount, currency)}</p>
            <div className="mt-1">
              <ExpenseStatusBadge status={expense.status} size="sm" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info Card */}
          <Card className="p-6 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-md rounded-xl">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                  Category
                </dt>
                <dd>
                  <Badge variant={CATEGORY_BADGE_VARIANT[expense.category] ?? 'default'}>
                    {CATEGORY_LABELS[expense.category]}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                  Location
                </dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">
                  {expense.location_name}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                  Description
                </dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">
                  {expense.description}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                  Submitted By
                </dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">
                  {expense.submitted_by_name}
                </dd>
              </div>
              {expense.reference && (
                <div>
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Reference
                  </dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                    {expense.reference}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Receipt */}
          {expense.receipt_url && (
            <Card className="p-6 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-md rounded-xl">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
                Receipt
              </h3>
              <div className="flex items-center gap-4">
                <img
                  src={expense.receipt_url}
                  alt="Expense receipt"
                  role="img"
                  aria-label="Expense receipt"
                  className="h-24 w-24 rounded-lg object-cover bg-neutral-100 dark:bg-neutral-700 cursor-pointer shadow-sm hover:shadow-md transition-shadow duration-200"
                  onClick={() => setShowReceiptModal(true)}
                />
                <Button variant="secondary" size="sm" onClick={() => setShowReceiptModal(true)}>
                  View Full Size
                </Button>
              </div>
            </Card>
          )}

          {/* Timeline */}
          <Card className="p-6 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-md rounded-xl">
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">
              Activity
            </h3>
            <Timeline items={timelineItems} />
          </Card>
        </div>

        {/* Right: Status + Approval */}
        <div className="space-y-4">
          {/* Status Card with colored accent */}
          <Card className={`p-5 rounded-xl shadow-md ${statusAccent.bg} ${statusAccent.darkBg} ${statusAccent.border}`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
              Status
            </h3>
            <ExpenseStatusBadge
              status={expense.status}
              approver={expense.approved_by_name}
              rejectionReason={expense.rejection_reason}
              timestamp={expense.approved_at ? new Date(expense.approved_at) : null}
            />

            {(expense.status === 'APPROVED' || expense.status === 'REJECTED') && (
              <div className="mt-3 pt-3 border-t border-neutral-200/50 dark:border-neutral-700/50 space-y-1">
                {expense.approved_by_name && (
                  <p className="text-xs text-neutral-500">
                    By <span className="font-medium text-neutral-700 dark:text-neutral-300">{expense.approved_by_name}</span>
                  </p>
                )}
                {expense.approved_at && (
                  <p className="text-xs text-neutral-400 tabular-nums">
                    {new Date(expense.approved_at).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                )}
                {expense.rejection_reason && (
                  <p className="text-xs text-danger-600 dark:text-danger-400 mt-1">
                    Reason: {expense.rejection_reason}
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Already approved/rejected alert */}
          {expense.status === 'APPROVED' && (
            <Alert title="This expense has been approved and impacts P&L." variant="info" />
          )}

          {/* Approval Actions */}
          {canApprove && (isPending || isOnHold) && (
            <Card className="p-5 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-md rounded-xl">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
                Approval
              </h3>
              {isSelfSubmitted ? (
                <Alert title="You cannot approve your own expense." variant="warning" />
              ) : (
                <ApprovalCard
                  approval={{
                    id: expense.id,
                    type: 'EXPENSE',
                    amount: expense.amount,
                    submittedBy: expense.submitted_by_name,
                    submittedAt: new Date(expense.created_at),
                    details: expense.description,
                  }}
                  isSelfSubmitted={false}
                  currency={currency}
                  loading={isMutating}
                  onApprove={() => approveMutation.mutate()}
                  onReject={(reason) => rejectMutation.mutate(reason)}
                  onHold={() => holdMutation.mutate()}
                />
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Receipt Full-Size Modal */}
      {expense.receipt_url && showReceiptModal && (
        <Modal open={showReceiptModal} onClose={() => setShowReceiptModal(false)} title="Receipt">
          <ReceiptImageViewer
            imageUrl={expense.receipt_url}
            expenseDetails={{
              amount: expense.amount,
              category: CATEGORY_LABELS[expense.category],
              date: new Date(expense.expense_date),
              submittedBy: expense.submitted_by_name,
              notes: expense.description,
            }}
            currency={currency}
            onClose={() => setShowReceiptModal(false)}
          />
        </Modal>
      )}
    </>
  );
}

export function ExpenseDetailPage() {
  return (
    <PageContainer
      title="Expense Detail"
      breadcrumbs={[{ label: 'Expenses', href: '/expenses' }, { label: 'Detail' }]}
    >
      <ErrorBoundary>
        <ExpenseDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
